// Canvas 9: Executive One-Pager. Seven zones in five steps. The ask comes first; every line names its source canvas;
// every number shows its tag. Zone 7 answers the top three open rows of the Buy-in Ledger. Check 6 (Traceability) reads the sources.
import { makeDef, G, text, long, num, pick, calc, fields, table, pull, note } from './base.js';
import { has, S, N, T, rows, fix, moneyM, sortGaps, ledgerTop as topOpen } from '../helpers.js';

// ---------------------------------------------------------------- content
const SOURCES = [
  'Case Card',
  'Canvas 1',
  'Canvas 2',
  'Canvas 3',
  'Canvas 4',
  'Canvas 5',
  'Canvas 6',
  'Canvas 7',
  'Canvas 8',
  'Evidence Gaps',
  'Ledger',
];
const WORDS_PER_MINUTE = 130;
const WORDS_IN_THREE = WORDS_PER_MINUTE * 3;
const WORDS_LIMIT = 150 * 3;

// What each zone is for, the sentence frame the learner writes into, the NorthStar line, and the NorthStar source and counts.
const ZONES = [
  {
    n: 1,
    name: 'The ask',
    sub: 'The decision, the sponsor, the funding figure, the date',
    frame:
      'Fund [what] for [journey]: [scope and length]. Sponsor: [role]. Funding: [figure] ([tag]), with [x] per cent contingency held by [role]. ' +
      'Decision wanted at [meeting, date].',
    line:
      'Fund Phase 1 of pre-fill and document reading for account opening: a six-month pilot in one region. Sponsor: Head of Retail Banking. ' +
      'Funding: S$0.2M to S$0.3M (E), with 25% contingency held by the CFO. Decision wanted at the November 2026 Executive Committee.',
    rule: 'A committee could act on it today: a decision, a sponsor, a funding figure and a date are all named.',
    trap: '"Support us" gives the committee nothing to decide. Name the decision, the money and the date.',
    source: 'Canvas 8',
    cell: 'Phase 1 tranche',
    nums: { e: '2' },
    numEx: 'E 2: the funding range and the contingency',
  },
  {
    n: 2,
    name: 'Why now',
    sub: 'The pressure and the evidence',
    frame: '[The pressure, with its number] against [the standard] ([tag]), and [what the board, the customer or the market will do if we wait].',
    line:
      'Complaints take 9 business days against a board standard of 3 (M), and the board will stop funding pilots ' +
      'with no shared plan and no governance built in.',
    rule: 'Every number has a baseline and a tag.',
    trap: 'A pressure with no number. Say how big it is, and tag it.',
    source: 'Case Card',
    cell: 'Section D, complaint resolution',
    nums: { m: '1' },
    numEx: 'M 1: nine business days',
  },
  {
    n: 3,
    name: 'What it enables',
    sub: 'For the customer and for the business',
    frame: '[The one change the customer sees, in plain words]; plus [the business gain, with its range] ([tag]), worth [value a year] ([tag]).',
    line: 'One sitting, no repeated questions; plus 5 to plus 12 points of completed applications (G), worth S$0.72M to S$1.73M a year (G).',
    rule: 'One customer-visible change, and the business gain with its range and its tag.',
    trap: 'A list of features. Say what changes for the customer, then what that is worth.',
    source: 'Canvas 4',
    cell: 'Money line, low and high case',
    nums: { g: '2' },
    numEx: 'G 2: the completion range and the value range',
  },
  {
    n: 4,
    name: 'How it scales',
    sub: 'The phases, and the gate that ends each',
    frame: '[Number] phases, each ended by a gate; Gate 1 releases [what] on [the measured evidence].',
    line: 'Three phases, each ended by a gate; Gate 1 releases Phase 2 on a measured completion gain.',
    rule: 'Each phase ends in a gate, and the gate says what it releases.',
    trap: 'A list of activities with no gate. The committee funds gates, not activities.',
    source: 'Canvas 8',
    cell: 'Gate 1 row',
    nums: {},
  },
  {
    n: 5,
    name: 'Value and key risk',
    sub: 'Numbers with tags; one risk, mitigated',
    frame: 'Value as zone 3, tagged [tag] until [when it is measured]. Key risk: [one risk]. Mitigation: [what, and who, by when].',
    line:
      'Value as zone 3, tagged G until Phase 1 measures it. Key risk: nobody owns explanation and appeal. ' +
      'Mitigation: Head of Risk named before Gate 1.',
    rule: 'One risk has a mitigation, and the mitigation has an owner.',
    trap: 'A list of risks. Pick the one that could stop the ask, and say who owns the answer.',
    source: 'Canvas 6',
    cell: 'Riskiest gap',
    nums: {},
  },
  {
    n: 6,
    name: 'Trade-off and assumption',
    sub: 'What we give up, and what we assume',
    frame: 'We do not start with [the highest-value idea] because [reason]. Assumption: [the one assumption that would hurt most].',
    line:
      'We do not start with the highest-value idea (use case 4) because it needs Tier 3 controls first. ' +
      'Assumption: the completion baseline leaves room to gain.',
    rule: 'One trade-off is stated, and one assumption.',
    trap: 'Saying there is no trade-off. Every plan gives something up: say what.',
    source: 'Canvas 3',
    cell: 'Top pick and second pick',
    nums: {},
  },
];
const OBJECTION_ZONE = {
  n: 7,
  name: 'Objections answered',
  sub: 'The top three open rows in your Ledger, each with its answer',
  source: 'Ledger',
  cell: 'Top three open rows',
  nums: {},
};

// ---------------------------------------------------------------- helpers (local to this file)
const fx = (up, n) => (up && up[n] && up[n].d && up[n].d.facts) || {};
const isNum = (x) => typeof x === 'number' && Number.isFinite(x);
const arr = (x) => (Array.isArray(x) ? x : []);
const lines = (list) => (list.length ? list.join('\n') : null);
const the = (s) => (/^the\s/i.test(s) ? s : `the ${s}`);
const wordCount = (s) => String(s || '').split(/\s+/).filter(Boolean).length;
const ledgerRows = (up) => {
  const a = (up && up.agg) || {};
  const top = Array.isArray(a.ledgerTop) ? a.ledgerTop : topOpen(arr(a.ledger), 3);
  return top.slice(0, 3);
};
const guesses = (up) => sortGaps(arr(up && up.agg && up.agg.gaps)).filter((g) => g.tag === 'G');

// ---------------------------------------------------------------- what each zone pulls from the pack
const baselineLine = (b) => {
  const val = b.value ? `${b.value}${b.unit ? ` ${b.unit}` : ''}` : 'not yet known';
  return `${b.measure}: ${val}${b.tag ? ` (${b.tag})` : ''}`;
};
const baselines = (up) => lines(arr(fx(up, 0).baselines).filter((b) => b.measure).map(baselineLine));
const maturity = (up) => {
  const f1 = fx(up, 1);
  if (!isNum(f1.weightedNow)) return null;
  let s = `Weighted level ${fix(f1.weightedNow, 1)}${f1.nowReads ? ` (${f1.nowReads})` : ''}`;
  if (isNum(f1.weightedTarget)) s += ` against a target of ${fix(f1.weightedTarget, 1)}`;
  if (f1.priorityGap) s += `; priority gap: ${f1.priorityGap}`;
  return s;
};
const picks = (up) => {
  const f3 = fx(up, 3);
  const top = f3.topPick;
  const second = f3.secondPick;
  if (!top || !top.name) return null;
  const out = [`Top pick: ${top.name}${top.flag ? ' (guardrail flag)' : ''}`];
  if (second && second.name) out.push(`Second pick: ${second.name}${second.flag ? ' (guardrail flag)' : ''}`);
  return lines(out);
};
const phase = (up, i) => arr(fx(up, 8).phases)[i] || null;
const tranche1 = (up) => {
  const p = phase(up, 0);
  return p && p.tranche ? `${p.tranche}${p.trancheTag ? ` (${p.trancheTag})` : ''}` : null;
};
const contingency = (up) => {
  const c8 = fx(up, 8).contingency;
  if (c8) return c8;
  const inv = fx(up, 4).invest || {};
  if (!isNum(inv.contingencyPct)) return null;
  return `${inv.contingencyPct}%${inv.contingencyHolder ? `, held by ${the(inv.contingencyHolder)}` : ''}`;
};
const valueText = (up) => {
  const f4 = fx(up, 4);
  const m = f4.money || {};
  if (!m.rangeText) return null;
  const half = isNum(m.halfLowValue) ? `; ${moneyM(m.halfLowValue, f4.cur || 'S$')} at half the low case` : '';
  return `${m.rangeText}${half}`;
};
const phasesText = (up) =>
  lines(
    arr(fx(up, 8).phases)
      .filter((p) => p.name)
      .map((p) => `Phase ${p.no}, ${p.name}, ${p.window}${p.tranche ? `: ${p.tranche}` : ''}`),
  );
const gate1Text = (up) => {
  const g = (phase(up, 0) || {}).gate;
  if (!g) return null;
  const out = [];
  if (g.value) out.push(`Value measure: ${g.value}`);
  if (g.launch) out.push(`Launch condition: ${g.launch}`);
  if (g.readiness) out.push(`Readiness measure: ${g.readiness}`);
  if (g.decider) out.push(`Decider: ${g.decider}`);
  if (g.releases) out.push(`It releases: ${g.releases}`);
  return lines(out);
};
const itemCounts = (up) => {
  const c = fx(up, 8).counts;
  return Array.isArray(c) && c.length === 3 ? `Items in Phases 1, 2 and 3: ${c.join(', ')}` : null;
};
const riskiest = (up) => {
  const f6 = fx(up, 6);
  return f6.riskiestGap ? `${f6.riskiestGap}${f6.gapOwner ? ` (owner: ${f6.gapOwner})` : ''}` : null;
};
const guessLines = (up) => {
  const list = guesses(up);
  if (!list.length) return null;
  const first = list.slice(0, 3).map((g) => `${g.item} (${g.where})`);
  return lines([`${list.length} Guesses still to measure. The first:`, ...first]);
};
const ledgerPull = (i) => (up) => {
  const r = ledgerRows(up)[i];
  if (!r) return null;
  const out = [`${r.where}, ${r.sceptic}${r.star ? ' (starred)' : ''}: ${r.objection}`];
  if (r.evidence) out.push(`Evidence: ${r.evidence}`);
  if (r.owner) out.push(`Follow-up owner: ${r.owner}`);
  return out.join('\n');
};
const PULLS = {
  1: [
    { id: 'sponsor', label: 'Sponsor', ref: 'Case Card C', no: 0, get: (up) => fx(up, 0).sponsor || null },
    { id: 'pick', label: 'Top pick', ref: 'Canvas 3', no: 3, get: (up) => (fx(up, 3).topPick && fx(up, 3).topPick.name) || null },
    { id: 'tranche', label: 'Phase 1 tranche', ref: 'Canvas 8', no: 8, get: tranche1 },
    { id: 'decider', label: 'Gate 1 decider', ref: 'Canvas 8', no: 8, get: (up) => ((phase(up, 0) || {}).gate || {}).decider || null },
    { id: 'contingency', label: 'Contingency', ref: 'Canvas 8', no: 8, get: contingency },
  ],
  2: [
    { id: 'baselines', label: 'Baselines', ref: 'Case Card D', no: 0, get: baselines },
    { id: 'maturity', label: 'Maturity', ref: 'Canvas 1', no: 1, get: maturity },
  ],
  3: [
    { id: 'journey', label: 'Journey', ref: 'Canvas 2', no: 2, get: (up) => fx(up, 2).journey || null },
    { id: 'vision', label: 'Vision', ref: 'Canvas 5', no: 5, get: (up) => fx(up, 5).vision || null },
    { id: 'value', label: 'Value a year', ref: 'Canvas 4', no: 4, get: (up) => (fx(up, 4).money || {}).rangeText || null },
    { id: 'frontline', label: 'Frontline sentence', ref: 'Canvas 7', no: 7, get: (up) => fx(up, 7).frontline || null },
  ],
  4: [
    { id: 'phases', label: 'The phases', ref: 'Canvas 8', no: 8, get: phasesText },
    { id: 'gate1', label: 'Gate 1, and what it releases', ref: 'Canvas 8', no: 8, get: gate1Text },
    { id: 'counts', label: 'Items in the roadmap', ref: 'Canvas 8', no: 8, get: itemCounts },
  ],
  5: [
    { id: 'value', label: 'Value a year', ref: 'Canvas 4', no: 4, get: valueText },
    { id: 'story', label: 'Value story', ref: 'Canvas 4', no: 4, get: (up) => fx(up, 4).valueStory || null },
    { id: 'gap', label: 'Riskiest gap and its owner', ref: 'Canvas 6', no: 6, get: riskiest },
    { id: 'guesses', label: 'Guesses to measure', ref: 'Evidence Gaps list', get: guessLines },
  ],
  6: [
    { id: 'picks', label: 'Top pick and second pick', ref: 'Canvas 3', no: 3, get: picks },
    { id: 'assumption', label: 'The assumption that would hurt most', ref: 'Canvas 4', no: 4, get: (up) => fx(up, 4).assumption || null },
    { id: 'stop', label: 'What we stop to make room', ref: 'Canvas 8', no: 8, get: (up) => fx(up, 8).stop || null },
  ],
  7: [0, 1, 2].map((i) => ({ id: `o${i + 1}`, label: `Open objection ${i + 1}`, ref: 'Ledger', get: ledgerPull(i) })),
};

// ---------------------------------------------------------------- the fields of one zone
const sourceField = (z) =>
  pick(`z${z.n}.source`, 'Source (a canvas)', SOURCES, {
    required: true,
    w: 4,
    guide: G(
      'Ring the canvas this line comes from, then add the cell below. A line with no source is cut, or goes back to an earlier canvas for its evidence.',
      z.source,
      'Check 6: every line names its source canvas.',
      'Naming the canvas you wrote the line on. Name the one that holds the evidence.',
    ),
  });
const cellField = (z) =>
  text(`z${z.n}.cell`, 'The cell it sits in', {
    w: 4,
    placeholder: 'For example: the Phase 1 tranche',
    guide: G('Name the cell that holds the figure or the fact, so a panel member can find it.', z.cell),
  });
const countField = (z, k, label, name) =>
  num(`z${z.n}.${k}`, label, {
    w: 1,
    min: 0,
    max: 20,
    guide: G(
      `Count the numbers on this line that carry the tag ${k.toUpperCase()} (${name}). Every number carries its tag.`,
      z.nums[k] ? z.numEx : `Blank: no number on this line carries the tag ${k.toUpperCase()}`,
      'A whole number from 0 to 20. A range counts once.',
      k === 'e' && z.n === 1 ? 'Counting a range as two numbers. S$0.2M to S$0.3M counts once.' : undefined,
    ),
  });
const zoneFields = (z) => [
  long(`z${z.n}.line`, 'Your line, in your own words', {
    required: true,
    w: 4,
    rows: 3,
    help: z.sub,
    placeholder: z.frame,
    guide: G(`Write one or two sentences in this shape: ${z.frame}`, z.line, z.rule, z.trap),
  }),
  sourceField(z),
  cellField(z),
  countField(z, 'm', 'Numbers tagged M', 'Measured'),
  countField(z, 'e', 'Numbers tagged E', 'Estimated'),
  countField(z, 'g', 'Numbers tagged G', 'Guess'),
];
const zoneBlocks = (z) => [pull(`pull${z.n}`, `Zone ${z.n}  ${z.name}: pull it from your pack`, PULLS[z.n]), fields(zoneFields(z))];

// ---------------------------------------------------------------- NorthStar (master spec, section 6; workbook ns_data.onepager)
const NORTHSTAR = {
  ...Object.fromEntries(
    ZONES.flatMap((z) => [
      [`z${z.n}.line`, z.line],
      [`z${z.n}.source`, z.source],
      [`z${z.n}.cell`, z.cell],
      ...Object.entries(z.nums).map(([k, x]) => [`z${z.n}.${k}`, x]),
    ]),
  ),
  ...T('obj', [
    { ans: 'All four are tagged G and are Phase 1 measurement tasks: Canvas 4 tags, Evidence Gaps list.' },
    { ans: 'Riskiest gap and launch conditions: Canvas 6.' },
    { ans: 'Core banking KYC data, Core banking lead: Canvas 3 data cell. Quality not yet checked.' },
  ], ['o1', 'o2', 'o3']),
  'z7.source': OBJECTION_ZONE.source,
  'z7.cell': OBJECTION_ZONE.cell,
  'reh.min': '2',
  'reh.sec': '5',
  'so.reviewer': 'CFO',
  'so.objection': 'If Phase 1 shows no completion gain by month 6, what stops?',
  'so.evidence': 'Gate 1 holds the Phase 2 tranche until the gain is measured: Canvas 8 gate row',
  'so.owner': 'Head of Retail Banking',
};

export default makeDef(9, {
  short: 'One-Pager',
  purpose:
    'Turn your pack into one page a committee can say yes to: the ask first, seven zones, every line traced to its source canvas, ' +
    'and a pitch you can give in three minutes.',
  howTo: [
    'Write the ask first. A committee should know what you want within ten seconds.',
    'Pull what you can from your pack, then write each line in your own words. Every line names its source canvas, and every number shows its tag.',
    'Answer the top three open rows in your Ledger. They are the objections most likely to stop the ask.',
    'Read the page aloud against a timer. Three minutes is the limit.',
  ],
  results: [
    { id: 'time', label: 'Rehearse once and time it', rule: 'Read it against a timer. Write the time.' },
    { id: 'inside', label: 'Inside three minutes?', rule: '180 seconds or under is inside.' },
    { id: 'lines', label: 'Lines with a source', rule: 'Count the zones with a source ringed.' },
    { id: 'numbers', label: 'Numbers, by tag', rule: 'Add up each column: M, E, G.' },
    {
      id: 'check6',
      label: 'Check 6: Traceability',
      rule: 'Pass: every line names its source canvas. Go back: find the line on an earlier canvas and name it, or cut the line.',
    },
  ],
  signoff: { mode: 'panel', ledger: false },
  steps: [
    {
      hint: 'Write the ask first: the decision, the sponsor, the funding figure and the date. Every other zone exists to support it.',
      blocks: [note('Every line names its source canvas. Every number shows its tag.', 'rule'), ...zoneBlocks(ZONES[0])],
    },
    {
      hint:
        'Say why now, what it enables and how it scales. Pull what you can, then write each line in your own words, ' +
        'with its source and the count of numbers by tag.',
      blocks: [...zoneBlocks(ZONES[1]), ...zoneBlocks(ZONES[2]), ...zoneBlocks(ZONES[3])],
    },
    {
      hint: 'Add the value and the key risk with its mitigation, then one trade-off and the one assumption you are making.',
      blocks: [...zoneBlocks(ZONES[4]), ...zoneBlocks(ZONES[5])],
    },
    {
      hint: 'Answer the top three open rows in your Ledger, one answer each, with the evidence and the cell it sits in.',
      blocks: [
        pull('pull7', 'Zone 7  Objections answered: the top three open rows in your Ledger', PULLS[7]),
        table('obj', {
          title: 'Your answer to each objection',
          rowLabel: 'Objection',
          rows: {
            from: (up) =>
              ledgerRows(up).map((r, i) => ({
                id: `o${i + 1}`,
                label: `${r.where}, ${r.sceptic}`,
                sub: r.objection,
              })),
          },
          pdf: 'cards',
          columns: [
            long('ans', 'Your answer, with the evidence and the cell it sits in', {
              required: true,
              w: 4,
              rows: 2,
              placeholder: '[The evidence]: [canvas and cell].',
              guide: G(
                'Answer in one or two sentences: the evidence first, then the canvas and the cell it sits in. Start from the evidence the Ledger row already holds.',
                'All four are tagged G and are Phase 1 measurement tasks: Canvas 4 tags, Evidence Gaps list.',
                'The objection most likely to block the ask is answered with evidence on the page.',
                'Promising to look into it. Point to the cell that answers it today, and say what is still open.',
              ),
            }),
          ],
        }),
        fields([
          pick('z7.source', 'Source (a canvas)', SOURCES, {
            required: true,
            w: 4,
            guide: G(
              'Ring the canvas the answers come from. For zone 7 it is the Ledger.',
              'Ledger',
              'Check 6: every line names its source canvas.',
              'Leaving it blank because the answers name their own canvases. Ring the Ledger as well.',
            ),
          }),
          text('z7.cell', 'The cell it sits in', {
            w: 4,
            guide: G('Name the part of the Ledger the answers come from.', 'Top three open rows'),
          }),
          countField(OBJECTION_ZONE, 'm', 'Numbers tagged M', 'Measured'),
          countField(OBJECTION_ZONE, 'e', 'Numbers tagged E', 'Estimated'),
          countField(OBJECTION_ZONE, 'g', 'Numbers tagged G', 'Guess'),
        ]),
      ],
    },
    {
      hint:
        'Read the whole page aloud against a timer and write the time. Over three minutes? Trim, and read it again. Rehearse once.',
      blocks: [
        fields([
          num('reh.min', 'Minutes', {
            required: true,
            w: 1,
            min: 0,
            max: 59,
            guide: G(
              'Read the page aloud once against a timer, then write the minutes your read-through took.',
              '2 (a read-through of 2 minutes 5 seconds)',
              'Three minutes is the limit.',
              'Reading it silently. Say it aloud: that is the pitch.',
            ),
          }),
          num('reh.sec', 'Seconds', {
            w: 1,
            min: 0,
            max: 59,
            guide: G('Write the seconds your read-through took. Leave blank if it was a whole number of minutes.', '5', '180 seconds or under is inside three minutes.'),
          }),
          long('reh.cut', 'What I cut to get inside three minutes', {
            w: 4,
            rows: 2,
            guide: G(
              'Write what you trimmed, or "Nothing: inside on the first read".',
              'Nothing: inside on the first read',
              undefined,
              'Cutting the ask. Cut detail from the zones, never the decision.',
            ),
          }),
          calc('words', 'Words in your seven zones', {
            w: 2,
            rule: `the words you wrote in zones 1 to 7; a calm pace is about ${WORDS_PER_MINUTE} words a minute, so three minutes holds about ${WORDS_IN_THREE}`,
            guide: G(
              'Worked out for you: the words in your lines and your objection answers, not the pulled text.',
              '200 or so words',
              `A calm speaking pace is about ${WORDS_PER_MINUTE} words a minute, so three minutes holds about ${WORDS_IN_THREE} words.`,
              'Trusting the count instead of the clock. Read it aloud.',
            ),
          }),
          calc('c6.say', 'Check 6 says', {
            w: 2,
            rule: 'the zones that name no source canvas, or Pass',
            guide: G(
              'Worked out for you. It names every zone that has a line and no source canvas.',
              'Pass',
              'Pass: every line names its source canvas. A failed check sends you upstream: find the line on an earlier canvas and name it, or cut the line.',
            ),
          }),
        ]),
      ],
    },
  ],
  derive(v, up) {
    const objAns = rows(v, 'obj')
      .map((r) => r.ans)
      .filter(Boolean);
    const zones = [...ZONES, OBJECTION_ZONE].map((z) => {
      const written = z.n < 7 ? has(S(v, `z${z.n}.line`)) : objAns.length > 0;
      const words = z.n < 7 ? wordCount(S(v, `z${z.n}.line`)) : objAns.reduce((a, x) => a + wordCount(x), 0);
      return {
        n: z.n,
        written,
        words,
        sourced: written && has(S(v, `z${z.n}.source`)),
        m: N(v, `z${z.n}.m`),
        e: N(v, `z${z.n}.e`),
        g: N(v, `z${z.n}.g`),
      };
    });
    const nWritten = zones.filter((z) => z.written).length;
    const nSourced = zones.filter((z) => z.sourced).length;
    const unsourced = zones.filter((z) => z.written && !z.sourced).map((z) => z.n);
    const anyNumber = zones.some((z) => z.m !== null || z.e !== null || z.g !== null);
    const sum = (k) => zones.reduce((a, z) => a + (z[k] || 0), 0);
    const words = zones.reduce((a, z) => a + z.words, 0);

    // Check 6, as the workbook's cell D26
    let check6 = '';
    if (nWritten > 0) {
      if (unsourced.length) {
        const where = unsourced.length === 1 ? `zone ${unsourced[0]} names` : `zones ${unsourced.join(', ')} name`;
        check6 = `Go back: ${where} no source canvas. Find the line on an earlier canvas and name it, or cut the line`;
      } else if (nWritten === 7) check6 = 'Pass';
    }

    // the rehearsal
    const mins = N(v, 'reh.min');
    const secs = N(v, 'reh.sec');
    const timed = mins !== null || secs !== null;
    const total = (mins || 0) * 60 + (secs || 0);
    const clock = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;

    const out = { calc: {}, results: {}, evidence: [], warnings: [], blockers: [], facts: {} };
    out.calc.words = words ? String(words) : '';
    out.calc['c6.say'] = check6;
    out.results = {
      time: timed ? clock : '',
      inside: timed ? (total <= 180 ? 'Inside three minutes' : 'Over three minutes: trim') : '',
      lines: nWritten ? `${nSourced} of 7` : '',
      numbers: anyNumber ? `M ${sum('m')}, E ${sum('e')}, G ${sum('g')}` : '',
      check6: check6 === '' ? '' : check6 === 'Pass' ? 'Pass' : 'Go back',
    };
    if (ledgerRows(up).length < 3 && (up && up.agg)) {
      out.warnings.push({ step: 's4', text: `The Ledger has ${ledgerRows(up).length} open rows. Log the objections from your sceptic rounds so that zone 7 has three.` });
    }
    if (words > WORDS_LIMIT) {
      out.warnings.push({ step: 's5', text: `${words} words is likely to run over three minutes at an ordinary pace. Trim, then read it again.` });
    }
    if (timed && total > 180) out.warnings.push({ step: 's5', text: 'Your read-through ran over three minutes. Trim, then read it again.' });
    return out;
  },
  NORTHSTAR,
});
